import React, { useEffect, useLayoutEffect, useState } from 'react';
import Ambassadors from './pages/Ambassadors';
import { ambassadors } from './services/ambassadors';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ErrorProvider } from './contexts/ErrorContext';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import UsersManagement from './pages/UsersManagement';
import SalesPersons from './pages/SalesPersons';
import SalesManagement from './pages/SalesManagement';
import Workshops from './pages/Workshops';
import MyWorkshops from './pages/MyWorkshops';
import MyCustomers from './pages/MyCustomers';
import Sliders from './pages/Sliders';
import Articles from './pages/Articles';
import ArticleEditor from './pages/blog/ArticleEditor';
import BlogCategories from './pages/blog/BlogCategories';
import BlogTags from './pages/blog/BlogTags';
import BlogComments from './pages/blog/BlogComments';
import BlogSettings from './pages/blog/BlogSettings';
import Podcasts from './pages/Podcasts';
import Courses from './pages/Courses';
import Assessments from './pages/Assessments';
import VipSurveys from './pages/VipSurveys';
import AssessmentReportPrint from './pages/assessments/AssessmentReportPrint';
import BusinessCases from './pages/BusinessCases';
import RecruitmentCriteria from './pages/RecruitmentCriteria';
import RecruitmentSubmissions from './pages/RecruitmentSubmissions';
import PortalAdmin from './pages/portal/PortalAdmin';
import PortalMentor from './pages/portal/PortalMentor';
import Invoices from './pages/Invoices';
import VideoPodcasts from './pages/VideoPodcasts';
import Comments from './pages/Comments';
import Logs from './pages/Logs';
import UploadCenter from './pages/UploadCenter';
import Settings from './pages/Settings';
import Categories from './pages/Categories';
import Layout from './components/Layout';
import ProtectedRoute, { homeFor } from './components/ProtectedRoute';
import Messages from './pages/Messages';
import Leads from './pages/Leads';
import PaymentLinks from './pages/PaymentLinks';
import SalesDashboard from './pages/SalesDashboard';
import WalletRequests from './pages/WalletRequests';
import WalletTransactions from './pages/WalletTransactions';
import Orders from './pages/Orders';
import Coupons from './components/CouponDiscountAdminPage';
import NotFound from './pages/NotFound';
import ErrorDisplay from './components/ErrorDisplay';
import APIErrorHandler from './components/APIErrorHandler';

function ScrollToTop() {
  const location = useLocation();
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname, location.search]);
  return null;
}

/**
 * دسترسی مسیرها بر اساس نقش.
 *
 * این لایهٔ دوم است؛ لایهٔ اول گاردهای `@Roles` روی خود اندپوینت‌های بک‌اند است
 * که داده را محافظت می‌کنند. این لایه جلوی دیدن صفحه‌هایی را می‌گیرد که برای
 * نقش کاربر معنایی ندارند — قبلاً منتور با تایپ دستی آدرس، پوستهٔ صفحه‌های
 * ادمین را می‌دید (هرچند داده‌ای نمی‌گرفت).
 *
 * فهرست‌ها عمداً با منوی کناری در Layout.tsx هم‌راستا هستند.
 */
const ADMIN = ['ADMIN'] as const;
const ADMIN_MENTOR = ['ADMIN', 'MENTOR'] as const;
/** فروشگاه فقط همین دو صفحه را دارد، ولی روی هر دو اختیار کامل دارد */
const ADMIN_STORE = ['ADMIN', 'STORE'] as const;
const ADMIN_MENTOR_STORE = ['ADMIN', 'MENTOR', 'STORE'] as const;
const ADMIN_SALES_MGR = ['ADMIN', 'SALES_MANAGER'] as const;
const ADMIN_SALES_PERSON = ['ADMIN', 'SALES_PERSON'] as const;
/** داشبورد برای همهٔ نقش‌های پنل باز است جز منتور که داشبورد خودش را دارد */
const DASHBOARD_ROLES = ['ADMIN', 'SALES_MANAGER', 'SALES_PERSON'] as const;

/** صفحهٔ خانهٔ هر نقش — منتور مستقیم به پنل خودش می‌رود، نه داشبورد ادمین */
function RoleHome() {
  const { user } = useAuth();
  const [ambassadorHome, setAmbassadorHome] = useState<boolean | null>(null);
  useEffect(() => {
    let live = true;
    if (user?.role === 'USER') {
      ambassadors.context().then(ctx => { if (live) setAmbassadorHome(ctx.roles.length > 0); }).catch(() => { if (live) setAmbassadorHome(false); });
    }
    return () => { live = false; };
  }, [user]);
  if (user?.role === 'USER') {
    if (ambassadorHome === null) return <div className="p-6">در حال دریافت دسترسی‌ها…</div>;
    if (ambassadorHome) return <Navigate to="/ambassadors" replace />;
  }
  return <Navigate to={homeFor(user?.role)} replace />;
}

/** میان‌بر برای بستن یک مسیر به چند نقش */
const Only: React.FC<{ roles: readonly string[]; children: React.ReactNode }> = ({
  roles,
  children,
}) => <ProtectedRoute roles={roles as any}>{children}</ProtectedRoute>;

function App() {
  return (
    <ErrorProvider>
      <AuthProvider>
        <APIErrorHandler />
        <ErrorDisplay />
        <Router>
          <ScrollToTop />
          <Routes>
            <Route path="/login" element={<Login />} />
            {/* صفحهٔ چاپ خارج از Layout است تا منو و هدر پنل در خروجی چاپ نیاید */}
            <Route
              path="/assessments/report/:attemptId"
              element={<Only roles={ADMIN_STORE}><AssessmentReportPrint /></Only>}
            />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Routes>
                      <Route path="/" element={<RoleHome />} />
                      <Route path="/ambassadors" element={<Ambassadors />} />
                      <Route path="/dashboard" element={<Only roles={DASHBOARD_ROLES}><Dashboard /></Only>} />
                      <Route path="/dashboard/categories" element={<Only roles={ADMIN}><Categories /></Only>} />
                      <Route path="/categories" element={<Navigate to="/dashboard/categories" replace />} />
                      <Route path="/sales-dashboard" element={<Only roles={ADMIN_SALES_MGR}><SalesDashboard /></Only>} />
                      <Route path="/users-management" element={<Only roles={ADMIN}><UsersManagement /></Only>} />
                      <Route path="/sales-persons" element={<Only roles={ADMIN}><SalesPersons /></Only>} />
                      <Route path="/sales-management" element={<Only roles={ADMIN}><SalesManagement /></Only>} />
                      <Route path="/workshops" element={<Only roles={ADMIN_SALES_MGR}><Workshops /></Only>} />
                      <Route path="/my-workshops" element={<Only roles={ADMIN_SALES_PERSON}><MyWorkshops /></Only>} />
                      <Route path="/my-customers" element={<Only roles={ADMIN_SALES_PERSON}><MyCustomers /></Only>} />
                      <Route path="/sales-report" element={<Only roles={ADMIN_SALES_MGR}><Dashboard /></Only>} />
                      <Route path="/my-sales-report" element={<Only roles={ADMIN_SALES_PERSON}><Dashboard /></Only>} />
                      <Route path="/sliders" element={<Only roles={ADMIN}><Sliders /></Only>} />
                      <Route path="/articles" element={<Only roles={ADMIN}><Articles /></Only>} />
                      <Route path="/articles/new" element={<Only roles={ADMIN}><ArticleEditor /></Only>} />
                      <Route path="/articles/:id/edit" element={<Only roles={ADMIN}><ArticleEditor /></Only>} />
                      <Route path="/articles/categories" element={<Only roles={ADMIN}><BlogCategories /></Only>} />
                      <Route path="/articles/tags" element={<Only roles={ADMIN}><BlogTags /></Only>} />
                      <Route path="/articles/comments" element={<Only roles={ADMIN}><BlogComments /></Only>} />
                      <Route path="/articles/settings" element={<Only roles={ADMIN}><BlogSettings /></Only>} />
                      <Route path="/podcasts" element={<Only roles={ADMIN}><Podcasts /></Only>} />
                      <Route path="/courses" element={<Only roles={ADMIN}><Courses /></Only>} />
                      <Route path="/assessments" element={<Only roles={ADMIN_STORE}><Assessments /></Only>} />
                      <Route path="/vip-surveys" element={<Only roles={ADMIN}><VipSurveys /></Only>} />
                      <Route path="/business-cases" element={<Only roles={ADMIN_MENTOR_STORE}><BusinessCases /></Only>} />
                      <Route path="/recruitment/criteria" element={<Only roles={ADMIN}><RecruitmentCriteria /></Only>} />
                      <Route path="/recruitment/submissions" element={<Only roles={ADMIN}><RecruitmentSubmissions /></Only>} />
                      <Route path="/portal-admin" element={<Only roles={ADMIN}><PortalAdmin /></Only>} />
                      <Route path="/portal-mentor" element={<Only roles={ADMIN_MENTOR}><PortalMentor /></Only>} />
                      <Route path="/coupons" element={<Only roles={ADMIN}><Coupons /></Only>} />
                      <Route path="/invoices" element={<Only roles={ADMIN}><Invoices /></Only>} />
                      <Route path="/video-podcasts" element={<Only roles={ADMIN}><VideoPodcasts /></Only>} />
                      <Route path="/comments" element={<Only roles={ADMIN}><Comments /></Only>} />
                      <Route path="/messages" element={<Only roles={ADMIN}><Messages /></Only>} />
                      <Route path="/leads" element={<Only roles={ADMIN}><Leads /></Only>} />
                      <Route path="/payment-links" element={<Only roles={ADMIN}><PaymentLinks /></Only>} />
                      <Route path="/wallet-requests" element={<Only roles={ADMIN}><WalletRequests /></Only>} />
                      <Route path="/wallet-transactions" element={<Only roles={ADMIN}><WalletTransactions /></Only>} />
                      <Route path="/orders" element={<Only roles={ADMIN}><Orders /></Only>} />
                      <Route path="/upload-center" element={<Only roles={ADMIN}><UploadCenter /></Only>} />
                      <Route path="/logs" element={<Only roles={ADMIN}><Logs /></Only>} />
                      <Route path="/settings" element={<Only roles={ADMIN}><Settings /></Only>} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Layout>
                </ProtectedRoute>
              }
            />
          </Routes>
        </Router>
      </AuthProvider>
    </ErrorProvider>
  );
}

export default App;
